import React, { useState, useEffect, useCallback, use } from "react";
import {
  faCircleCheck,
  faCircleXmark,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../../hoc";
import PageHeading from "../../../components/PageHeader/PageHeader";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../../components/NoData/NoData";
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  TextInput,
  ResetButton,
  MessageBox,
  Dropdown,
  CustomButton,
  Button,
  CreateButton,
} from "../../../components/FormElements";
import { TableBuilder } from "../../../components/TableElements";
import Modal from "../../../components/Modal";
import OverlayLoader from "../../../components/OverlayLoader/OverlayLoader";
import {
  StyledSpan,
  StyledModalContainer,
  StyledFiltersContainer,
  StyledContent,
} from "../../../components/Styled";
import Pagination from "../../../components/Pagination/Pagination";
import API from "../../../api";
import { API_RESPONSE_TYPES } from "../../../constants";
import {
  getSearchParams,
  shouldRedirect,
  redirectToPageOne,
} from "../../../utils";
import FiltersButton from "../../../components/FiltersButton/FiltersButton";
import moment from "moment";
import StatusHighliter from "../../../components/StatusHighliter/StatusHighliter";
import JSONPretty from "react-json-pretty";

const UsersRazorPayBankAccounts = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [data, setData] = useState([]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("Pending");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [remark, setRemark] = useState("");
  const [updateApprovalStatus, setUpdateApprovalStatus] = useState("");

  const [users, setUsers] = useState([]);
  const [fUsersId, setFusersId] = useState("");

  const readUsers = useCallback(() => {
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.get("/accounts-management/users?pageNumber=1&recordsPerPage=100000")
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setUsers(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus("");
    setResponseMessage("");
  };

  const resetFilters = () => {
    setFusersId("");
    setApprovalStatus("");
    readData();
  };

  const readData = useCallback(
    (fUsersId = "", approvalStatus = "") => {
      setIsLoading(true);
      setResponseStatus("");
      setResponseMessage("");
      setData([]);
      API.get(
        `/accounts-management/users/users-razorpay-accounts?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fUsersId=${fUsersId}`
      )
        .then((response) => {
          const { status, message, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.FAILED) {
              setResponseStatus(status);
              setResponseMessage(message);
            } else {
              setTotalRecords(pageInfo.totalRecords);
            }
            setData(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILED);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const reloadData = () => {
    readData(fUsersId, approvalStatus);
  };

  const updateData = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.put(`/accounts-management/users/users-bank-accounts/${modalData.id}`, {
      approvalStatus:
        updateApprovalStatus === "Approve" ? "Approved" : "Rejected",
      remark,
    })
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          reloadData();
          setShowModal(false);
          setModalData(null);
          setModalType("");
          setUpdateApprovalStatus("");
          setRemark("");
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(fUsersId, approvalStatus);
    }
  }, [paginationData, readData]);

  useEffect(() => {
    readUsers();
  }, [readUsers]);

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  const usersOptions = [{ title: "-- SELECT USER --", value: "" }];
  users.forEach(({ id, fullName, mobileNumber }) =>
    usersOptions.push({
      title: fullName,
      value: id?.toString(),
      subTitle: mobileNumber?.toString()
    })
  );

  return (
    <>
      <PageHeading {...props} reloadData={reloadData} />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? "show" : ""}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>User</Label>
            <Dropdown
              value={fUsersId}
              onChange={setFusersId}
              placeholder="User"
              disabled={isLoading}
              options={usersOptions}
              searchable
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton
              disabled={isLoading}
              type="button"
              onClick={resetFilters}
            >
              Clear
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={
          responseStatus === "" && !showModal && data?.length === 0 && isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILED &&
          !isLoading &&
          data?.length === 0
        }
        message={`No records found`}
      />
      {!showModal && responseStatus === API_RESPONSE_TYPES.FAILED && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {data?.length > 0 && (
        <>
          <TableBuilder
            isLoading={!showModal && data?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: "User Id",
                dataSelector: "id",
                align: "center",
                sticky: true,
                canSort: false,
                width: "100px",
                // CellRenderer: (value, item) => (
                //   <>
                //     <CustomButton
                //       icon={faEye}
                //       onClick={() => {
                //         setShowModal(true);
                //         setModalData(item);
                //         setModalType("BANK_FILE");
                //       }}
                //       style={{ color: "gray" }}
                //     />
                //     {item.approvalStatus === "Pending" && (
                //       <>
                //         <CustomButton
                //           icon={faCircleCheck}
                //           onClick={() => {
                //             setShowModal(true);
                //             setModalData(item);
                //             setUpdateApprovalStatus("Approve");
                //             setModalType("UPDATE");
                //           }}
                //           color="forestgreen"
                //           hoverColor="#006200"
                //         />
                //         <CustomButton
                //           icon={faCircleXmark}
                //           onClick={() => {
                //             setShowModal(true);
                //             setModalData(item);
                //             setUpdateApprovalStatus("Reject");
                //             setModalType("UPDATE");
                //           }}
                //           color="red"
                //           hoverColor="#a90404"
                //         />
                //       </>
                //     )}
                //   </>
                // ),
              },
              {
                title: "User",
                dataSelector: "firmName",
                dataType: "string",
                CellRenderer: (value, item) => (
                  <>
                    {value}
                    <StyledSpan>{item.fullName}</StyledSpan>
                    <StyledSpan>{item.mobileNumber}</StyledSpan>
                  </>
                ),
              },
              {
                title: "Customer Id",
                dataSelector: "customer_id",
                dataType: "string",
              },
              {
                title: "Account Id",
                dataSelector: "razorpay_virtual_id",
                dataType: "string",
              },
              
              {
                title: "Account Number",
                dataSelector: "bank_account_number",
                dataType: "string",
              },
              {
                title: "IFSC Code",
                dataSelector: "bank_ifsc",
                dataType: "string",
              },
              {
                title: "Date & Time",
                dataSelector: "createdOn",
                dataType: "string",
              },

            ]}
            tableData={data}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
          <Modal
            modalStatus={showModal}
            setModalStatus={() => {
              resetResponseData();
              setShowModal(false);
              setModalData(null);
              setRemark("");
              setModalType("");
              setApprovalStatus("");
            }}
            hideCloseButton
          >
            <StyledModalContainer width={"500px"}>
              {modalType === "BANK_FILE" ? (
                <>
                  <h3>Bank File</h3>
                  <FormContainer
                    style={{ overflowY: "scroll", maxHeight: "500px" }}
                  >
                    <Form method="POST" action="#" onSubmit={updateData}>
                      {modalData?.bankFile ? (
                        <StyledContent>
                          <JSONPretty
                            id="json-pretty"
                            data={
                              typeof modalData?.bankFile === "string"
                                ? JSON.parse(modalData?.bankFile)
                                : modalData?.bankFile
                            }
                          ></JSONPretty>
                        </StyledContent>
                      ) : (
                        <Fieldset>
                          <Label>No bank file found</Label>
                        </Fieldset>
                      )}
                    </Form>
                  </FormContainer>
                </>
              ) : (
                <>
                  <h3>{updateApprovalStatus} Request</h3>
                  <FormContainer>
                    <Form method="POST" action="#" onSubmit={updateData}>
                      <Fieldset>
                        <Label required={updateApprovalStatus !== "Approve"}>
                          Remark
                        </Label>
                        <TextInput
                          value={remark}
                          onChange={setRemark}
                          placeholder="Please enter remark"
                          disabled={showModal && isLoading}
                        />
                      </Fieldset>
                      {showModal && responseStatus && (
                        <Fieldset>
                          <MessageBox
                            status={showModal && responseStatus}
                            message={showModal && responseMessage}
                          />
                        </Fieldset>
                      )}
                      <Fieldset>
                        <CreateButton
                          disabled={showModal && isLoading}
                          type="submit"
                        >
                          Submit
                        </CreateButton>
                      </Fieldset>
                    </Form>
                    <OverlayLoader showLoader={showModal && isLoading} />
                  </FormContainer>
                </>
              )}
            </StyledModalContainer>
          </Modal>
        </>
      )}
    </>
  );
};

export default withAppHOC(UsersRazorPayBankAccounts);
